import { useQuery, useQueries } from "react-query";
import axiosInstance from "../axios/axiosInstance";
import { getAuthToken, getCurUser } from "../utilities/auth";

// common function to fetch games
// applies filters for visible games and any additional params passed in
const fetchGames = async (params = "") => {
  const separator = params ? "&" : "";
  const { data } = await axiosInstance.get(
    `games?filters[visible][$eq]=true${separator}${params}`
  );
  return data;
};

const getGamesFn = () => fetchGames("featuredGame=true&editorsPick=true");

export function useGames() {
  return useQuery(["games", 1], getGamesFn);
}

const getFilteredGamesFn = (filter) => {
  return fetchGames(`filters[categories][name][$eq]=${filter}`);
}

// shall we move the burden of filtering to the backend?
export function useFilteredGames(filter) {
  const queryKey = ["filteredGames", { category: filter }];
  return useQuery(queryKey, () => getFilteredGamesFn(filter));
}

const getSingleGameFn = async (gameId) => {
  const { data } = await axiosInstance.get(`games/${gameId}`);
  return data;
};

export function useSingleGame(gameId) {
  return useQuery("game", () => getSingleGameFn(gameId));
}

export function useCategories() {
  return useQuery("categories", async () => {
    const { data } = await axiosInstance.get(`categories`);
    return data;
  });
}

export function useGamesByCategories() {
  const { data: gamesData, isLoading: isGamesLoading } = useGames();
  const { data: categoriesData, isLoading: isCategoriesLoading } = useCategories();

  if (isGamesLoading || isCategoriesLoading || !gamesData || !categoriesData) {
    return { isLoading: true, categories: [], gamesByCategory: {} };
  }

  const categories = categoriesData.data
    .map(cat => cat.attributes.name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const gamesByCategory = {};
  categories.forEach(category => {
    gamesByCategory[category] = gamesData.games.filter(game =>
      Array.isArray(game.categories) &&
      game.categories.some(catObj => catObj.name === category)
    );
  });

  return { isLoading: false, categories, gamesByCategory };
}


// Fetch games the user has played (from leaderboard entries)
export function usePlayedGames() {
  const user = getCurUser();
  const userId = user?.id;
  
  const { data: playedGameIds = [], isLoading: idsLoading } = useQuery([
    "userRecentGames",
    userId
  ], async () => {
    if (!userId) return [];
    const { data } = await axiosInstance.get(`/leaderboards/user/${userId}`);
    return data.map(item => item.game.id);
  }, { enabled: !!userId });

  const results = useQueries(
    (playedGameIds || []).map((id) => ({
      queryKey: ["game", id],
      queryFn: () => getSingleGameFn(id),
      enabled: !!id
    }))
  );

  const isLoading = idsLoading || results.some(r => r.isLoading);
  const data = results.map(r => r.data);
  return { data, isLoading };
}